import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Quote } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { PageHero } from '../components/blocks/PageHero';
import { CtaBanner } from '../components/blocks/CtaBanner';
import { SectionDivider, SURFACE } from '../components/blocks/SectionDivider';
import { useSeo } from '../lib/useSeo';
import { reviewSchema } from '../lib/schema';
import { testimonials } from '../data/testimonials';

/**
 * PAGE: /testimonials
 * WORDPRESS: page template `page-testimonials.php`, looping the
 * `testimonial` CPT.
 *
 * Blocks: hero → testimonial_full[] → cta_banner
 *
 * Emits schema.org Review entities (not AggregateRating — these testimonials
 * carry no star ratings, and inventing them risks a manual action).
 */

const HERO_IMAGE = 'https://images.unsplash.com/photo-1500964757637-c85e8a162699?auto=format&fit=crop&q=80&w=2000';
export function Testimonials() {
  useSeo({
    title: 'Testimonials',
    description: "Read what people share about their experiences with Milangela's Soul Garden — first-hand accounts of Biofield Tuning, guided meditation and Sopotje sessions.",
    path: '/testimonials',
    image: HERO_IMAGE,
    breadcrumbs: [{
      name: 'Testimonials',
      path: '/testimonials'
    }],
    schema: reviewSchema(testimonials.map(testimonial => ({
      author: testimonial.name,
      body: testimonial.full,
      location: testimonial.location
    })))
  });
  const location = useLocation();
  useEffect(() => {
    if (!location.hash) return;
    const element = document.getElementById(location.hash.replace('#', ''));
    if (!element) return;
    const timer = window.setTimeout(() => element.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    }), 100);
    return () => window.clearTimeout(timer);
  }, [location]);
  return <div className="flex min-h-screen flex-col bg-cream" data-id="element-661" data-mp="30unu84knd0b3">
      <Header data-id="element-662" data-mp="2b7jqjdrcmag3" />

      <main data-id="element-663" data-mp="2b70kovez3cif">
        <PageHero heading="Testimonials" subheading="Stories of presence, resonance, and quiet transformation." image={HERO_IMAGE} imageAlt="Sunlight filtering through a quiet forest" align="center" size="compact" dividerFill={SURFACE.cream} data-id="element-664" data-mp="2b6s8z6ul6d8t" />

        <section className="bg-cream px-6 py-20" data-id="element-665" data-mp="2g7o9dafqbh9h">
          <div className="mx-auto max-w-3xl space-y-16" data-id="element-666" data-mp="1sgsl2nahf46z">
            {testimonials.map(testimonial => <article key={testimonial.id} id={testimonial.id} className="scroll-mt-32 rounded-3xl bg-white p-10 shadow-soft md:p-14" data-id="element-667" data-mp="1nj0jf1k1c3pl">
                <Quote size={36} strokeWidth={1} className="mb-6 text-rose-600" aria-hidden="true" data-id="element-668" data-mp="1xg56d3lpg37g" />
                <blockquote className="space-y-5 whitespace-pre-line font-serif text-lg italic leading-relaxed text-charcoal md:text-xl" data-id="element-669" data-mp="14s9d68vcgtff">
                  {testimonial.full}
                </blockquote>
                <footer className="mt-8 border-t border-cream-200 pt-6" data-id="element-670" data-mp="3l6bbci0z0i9f">
                  <p className="font-serif text-2xl text-charcoal" data-id="element-671" data-mp="36gx53isoygy9">
                    {testimonial.name}
                  </p>
                  {testimonial.location && <p className="mt-1 text-sm tracking-wide text-charcoal-50" data-id="element-672" data-mp="3bdxadrhpvfob">
                      {testimonial.location}
                    </p>}
                </footer>
              </article>)}
          </div>
        </section>

        <CtaBanner heading="Perhaps this is where your own story begins." body="Whether you are seeking balance, clarity or simply a quiet place to pause, you are welcome exactly as you are." buttonLabel="Start Your Journey" buttonTo="/start-your-journey" dividerFrom={SURFACE.cream} data-id="element-673" data-mp="3q3uspqsytb5l" />
      </main>

      <Footer data-id="element-674" data-mp="2yyh1lw81ajwm" />
    </div>;
}